import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '../lib/utils';

const DrawerContext = React.createContext<{
    direction?: 'top' | 'bottom' | 'left' | 'right' | undefined;
}>({});

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
    className?: string;
};

function Drawer({
    shouldScaleBackground = true,
    dismissible = true,
    ...props
}: DrawerProps) {
    return (
        <DrawerContext value={{ direction: props.direction }}>
            <DrawerPrimitive.Root
                shouldScaleBackground={shouldScaleBackground}
                dismissible={dismissible}
                {...props}
            />
        </DrawerContext>
    );
}
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

export type DrawerOverlayProps = React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & { ref?: React.RefObject<React.ComponentRef<typeof DrawerPrimitive.Overlay> | null> };

function DrawerOverlay({ ref, className, ...props }: DrawerOverlayProps) {
    return (
        <DrawerPrimitive.Overlay
            ref={ref}
            className={cn('fixed inset-0 z-50 bg-black/80', className)}
            {...props}
        />
    );
}
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

export type DrawerContentProps = React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & { asChild?: boolean; ref?: React.RefObject<React.ComponentRef<typeof DrawerPrimitive.Content> | null> };

function DrawerContent({ ref, className, children, asChild, ...props }: DrawerContentProps) {
    const { direction } = React.use(DrawerContext);

    return (
        <DrawerPortal>
            <DrawerOverlay />
            <DrawerPrimitive.Content
                ref={ref}
                className={cn(
                    'fixed z-50 flex max-h-[100%] transform overflow-hidden border bg-background-primary shadow-lg transition-transform duration-300 ease-in-out',
                    {
                        'inset-x-0 top-0 -mt-16 h-auto flex-col-reverse rounded-b-[10px] pt-16 sm:mb-24':
                            direction === 'top',
                        'inset-y-0 right-0 -mr-16 flex w-auto rounded-l-[10px] pr-16 sm:ml-24':
                            direction === 'right',
                        'inset-x-0 bottom-0 -mb-16 flex h-auto flex-col rounded-t-[10px] pb-16 sm:mt-24':
                            !direction || direction === 'bottom',
                        'inset-y-0 left-0 -ml-16 flex w-auto flex-row-reverse rounded-r-[10px] pl-16 sm:mr-24':
                            direction === 'left',
                    },
                )}
                {...props}
            >
                <div className={cn('flex items-center justify-center p-2')}>
                    <div
                        className={cn('rounded-md bg-background-quaternary', {
                            'h-1 w-1/2 cursor-n-resize':
                                !direction
                                || direction === 'bottom'
                                || direction === 'top',
                            'h-1/3 w-1 cursor-e-resize':
                                direction === 'left' || direction === 'right',
                        })}
                    />
                </div>
                <div
                    className={cn(
                        'flex h-full w-full flex-col overflow-auto',
                        {
                            'py-4':
                                direction === 'left' || direction === 'right',
                            'pt-4': direction === 'top',
                            'pr-6': direction === 'right',
                            'pb-4': direction === 'bottom',
                            'pl-6': direction === 'left',
                        },
                        className,
                    )}
                >
                    {children}
                </div>
            </DrawerPrimitive.Content>
        </DrawerPortal>
    );
}
DrawerContent.displayName = 'DrawerContent';

function DrawerHeader({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)}
            {...props}
        />
    );
}
DrawerHeader.displayName = 'DrawerHeader';

function DrawerFooter({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            className={cn('mt-auto flex flex-col gap-2 p-4', className)}
            {...props}
        />
    );
}
DrawerFooter.displayName = 'DrawerFooter';

function DrawerTitle({ ref, className, ...props }: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & { ref?: React.RefObject<React.ElementRef<typeof DrawerPrimitive.Title> | null> }) {
    return (
        <DrawerPrimitive.Title
            ref={ref}
            className={cn(
                'text-lg font-semibold leading-none tracking-tight',
                className,
            )}
            {...props}
        />
    );
}
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

function DrawerDescription({ ref, className, ...props }: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> & { ref?: React.RefObject<React.ElementRef<typeof DrawerPrimitive.Description> | null> }) {
    return (
        <DrawerPrimitive.Description
            ref={ref}
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    );
}
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerPortal,
    DrawerTitle,
    DrawerTrigger,
};
